import InputField from "../../common/InputField";
import { notify } from "../../common/Toast";
import { Button } from "../../common/Shared";

export const ProductionCard = ({
  row,
  index,
  isModal = false,
  handleSave,
  handleActionChange,
  handleProdStart,
  handleRowChange,
  handleSaveBilletChange,
  calcFinal,
  safeDiv,
  isCastingData,
  dieUnloadReason,
  inProductionData,
  handleDieUnload,
  isSaving = false,
}) => (
  <div
    key={index}
    className="bg-white border border-gray-100 mb-6 -3px_rgba(0,0,0,0.07)] rounded-sm overflow-hidden"
  >
    {/* Card Sub-Header: High-Density Industrial Metrics */}
    <div className="bg-gray-50/80 px-6 py-2.5 border-b border-gray-100 flex flex-wrap justify-between items-center gap-4">
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex flex-col">
          <span className="text-[8px] font-black text-gray-400 uppercase tracking-[2px] mb-0.5">Order</span>
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-black text-slate-800">{row.order_No}</span>
            {row.order_Status && (
              <span className={`px-2 py-0.5 text-[8px] font-black uppercase tracking-widest border ${row.order_Status === 'RUNNING' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-green-50 text-green-600 border-green-100'
                }`}>
                {row.order_Status}
              </span>
            )}
          </div>
        </div>

        <div className="h-8 w-px bg-gray-200 hidden xl:block"></div>

        <div className="flex flex-wrap gap-8">
          {[
            { label: "Batch", value: row.batch_Id, color: "text-slate-800" },
            { label: "Profile", value: row.profile_Name, color: "text-slate-600" },
            { label: "Die Ref", value: row.dieNumber, color: "text-primary" },
            { label: "Pcs / Wt", value: `${row.qty_Pcs} / ${row.qty_Weight} KG`, color: "text-orange-700" },
            { label: "Qty Wt", value: `${row.qty_Weight}`, color: "text-emerald-700" },
            { label: "Spec", value: `${row.cut_Length}`, color: "text-yellow-700" },
            { label: "Current Die Wt", value: `${row.current_Die_Weight}`, color: "text-pink-700" },
          ].map((item, i) => (
            <div key={i} className="flex flex-col">
              <span className="text-[8px] font-black text-gray-400 uppercase tracking-[2px] mb-0.5">{item.label}</span>
              <span className={`text-[12px] font-bold ${item.color}`}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {isModal ||
          row.order_Status === "RUNNING" ||
          row.order_Status === "COMPLETED" ? (
          <>
            {!isModal && (
              <select
                className="bg-white text-gray-700 border border-gray-200 px-3 py-2 text-[10px] font-black uppercase tracking-[1px] focus:border-primary outline-none cursor-pointer transition-all"
                onChange={(e) => handleActionChange(index, e.target.value)}
                value={row.selectedAction || ""}
                data-tour="actions-menu"
              >
                <option value="">Actions</option>
                <option
                  value="billet_change"
                  disabled={row.order_Status === "COMPLETED"} className={`${row.order_Status === "COMPLETED" ? "text-slate-400" : ""}`}
                >
                  Billet Change
                </option>
                <option
                  value="die_unload"
                  disabled={row.order_Status === "COMPLETED"}
                  className={`${row.order_Status === "COMPLETED" ? "text-slate-400" : ""}`}>
                  Die Unload
                </option>
                <option value="mark_done">Mark Done</option>
              </select>
            )}

            <Button
              onClick={() => {
                if (isModal) {
                  return handleSaveBilletChange();
                }

                const act = inProductionData[index]?.selectedAction;
                const reason = inProductionData[index]?.die_Unload_Reason_Id;

                if (act === "die_unload") {
                  if (!reason) return notify("Unload reason required!", "error");
                  return handleDieUnload(inProductionData[index], index);
                } else {
                  handleSave(row, index);
                }
              }}
              className="whitespace-nowrap"
              loading={isSaving}
              disabled={isSaving}
              data-tour="log-production-btn"
            >
              {isModal ? "CONFIRM EXCHANGE" : "SAVE"}
            </Button>
          </>
        ) : (
          !isModal && (
            <Button
              className="whitespace-nowrap"
              onClick={() => handleProdStart(row, index)}
              loading={isSaving}
              disabled={isSaving}
            >
              Prod. Start
            </Button>
          )
        )}
      </div>
    </div>

    {/* Technical Form Grid */}
    <div className="p-6 bg-white">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        <div data-tour="casting-no">
          <InputField
            label="Casting No"
            type="select"
            options={isCastingData}
            value={row.casting_No}
            onChange={(val) => handleRowChange(index, "casting_No", val)}
            required
          />
        </div>

        <InputField
          label="Die In"
          type="time"
          value={row.die_In}
          onChange={(e) => handleRowChange(index, "die_In", e)}
        />

        <InputField
          label="Die Out"
          type="time"
          value={row.die_Out}
          onChange={(e) => handleRowChange(index, "die_Out", e)}
        />

        <div data-tour="pcs-needed">
          <InputField
            label="Pcs Needed"
            value={row.pcs_Needed}
            onChange={(e) => handleRowChange(index, "pcs_Needed", e)}
          />
        </div>

        <div className="flex flex-col" data-tour="billet-spec">
          <InputField
            label="Billet Size"
            value={row.billet_Size}
            onChange={(e) => handleRowChange(index, "billet_Size", e)}
            required
          />
          {(() => {
            const v = calcFinal(row);
            return (
              v !== null && (
                <div className="mt-1 flex items-center gap-1.5 text-gray-400 group">
                  <span className="text-[8px] uppercase font-black tracking-widest group-hover:text-primary transition-colors">Size:</span>
                  <span className="text-[10px] font-black text-slate-700">{v.toFixed(1)}"</span>
                </div>
              )
            );
          })()}
        </div>

        <div className="flex flex-col">
          <InputField
            label="No Of Billets"
            value={row.no_Of_Billet}
            onChange={(e) => handleRowChange(index, "no_Of_Billet", e)}
            required
          />
          {(() => {
            const ratio = safeDiv(row.qty_Pcs, row.pcs_Needed);
            return (
              ratio !== null && (
                <div className="mt-1 flex items-center gap-1.5 text-gray-400 group">
                  <span className="text-[8px] uppercase font-black tracking-widest group-hover:text-emerald-500 transition-colors">Pcs:</span>
                  <span className="text-[10px] font-black text-slate-700">{ratio.toFixed(0)}</span>
                </div>
              )
            );
          })()}
        </div>

        <InputField
          label="Input Wt"
          value={row.input_Weight}
          disabled
        />

        <div data-tour="output-logging">
          <InputField
            label="No Of Pcs"
            value={row.no_Of_Pcs}
            onChange={(e) => handleRowChange(index, "no_Of_Pcs", e)}
          />
        </div>

        <InputField
          label="Output Wt"
          value={row.output_Weight}
          onChange={(e) => handleRowChange(index, "output_Weight", e)}
        />
        <InputField label="Recov (%)" value={row.recovry} disabled />


        <InputField
          label="But End Rej."
          value={row.but_End_Rejection_Mm}
          onChange={(e) => handleRowChange(index, "but_End_Rejection_Mm", e)}
        />
        <InputField
          label="Stretch Rej."
          value={row.stertching_Rejection_Ft}
          onChange={(e) => handleRowChange(index, "stertching_Rejection_Ft", e)}
        />
        <InputField
          label="Ext. Speed"
          value={row.ext_Speed}
          onChange={(e) => handleRowChange(index, "ext_Speed", e)}
        />

        <InputField
          label="Billet Temp"
          value={row.billet_Temp}
          onChange={(e) => handleRowChange(index, "billet_Temp", e)}
        />

        <div className="xl:col-span-2">
          <InputField
            type="textarea"
            label="Remarks"
            value={row.remark}
            onChange={(e) => handleRowChange(index, "remark", e)}
          />
        </div>

        <div className="xl:col-span-1">
          <InputField
            type="select"
            label="Unload Reason"
            placeholder="Select reason"
            required
            options={dieUnloadReason}
            value={row.die_Unload_Reason_Id || ""}
            onChange={(val) => {
              handleRowChange(index, "die_Unload_Reason_Id", val);
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

